import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { MoreVerticalIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@/entities/user';
import type { GuidelineResponseDto } from '@/lib/api/guidelines-client';

export interface GuidelineRowActionsProps {
  guideline: GuidelineResponseDto;
  onEdit: (guideline: GuidelineResponseDto) => void;
  onDelete: (guideline: GuidelineResponseDto) => void;
}

export function GuidelineRowActions({ guideline, onEdit, onDelete }: GuidelineRowActionsProps) {
  const { t } = useTranslation('knowledge');

  const { canManageBrandVisualGuidelines } = usePermissions();
  const noPermissionTooltip = canManageBrandVisualGuidelines
    ? ''
    : t('guidelines.guidelineActionMenu.youDonTHavePermission');

  const menu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="mini"
          aria-label={t('guidelines.guidelineRowActions.guidelineActions')}
          className="size-9 p-0"
          onClick={(event) => event.stopPropagation()}
        >
          <MoreVerticalIcon className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
        <DropdownMenuItem
          disabled={!canManageBrandVisualGuidelines}
          onSelect={() => onEdit(guideline)}
        >
          {t('guidelines.guidelineActionMenu.edit')}
        </DropdownMenuItem>
        <DropdownMenuItem
          variant="destructive"
          disabled={!canManageBrandVisualGuidelines}
          onSelect={() => onDelete(guideline)}
        >
          {t('guidelines.guidelineActionMenu.delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (!noPermissionTooltip) return menu;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">{menu}</span>
        </TooltipTrigger>
        <TooltipContent>{noPermissionTooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

GuidelineRowActions.displayName = 'GuidelineRowActions';
